import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HiLocationMarker,
  HiOfficeBuilding,
  HiClock,
  HiExclamation,
  HiMinusCircle,
  HiPlusCircle,
} from "react-icons/hi";
import { EnhancedRouteItem, STYLE_CLASSES } from "./types";
import { formatStopTimingLabel } from "../../helpers/routeMappers";
import type { TranslateFn } from "../../types";
import { StatusBadge } from "@components";
import { statusDelivery, statusPickup, Inline } from "@design-system";
import { useStopValidation } from "./hooks/useStopValidation";

// Helper function to format timing information
const formatTimingInfo = (stop: EnhancedRouteItem, t: TranslateFn): string =>
  formatStopTimingLabel(stop.timing, t, { variant: "planner" }) || "";

export interface StopCardProps {
  stop: EnhancedRouteItem;
  onMoveToRoute?: () => void;
  onMoveToAvailable?: () => void;
  isInvalid?: boolean;
  routeStops: EnhancedRouteItem[];
  isDragging?: boolean;
  onRemoveItem?: () => void;
  isAssignedCard?: boolean;
  onAddItem?: () => void;
}

export const StopCardContent: FC<StopCardProps> = ({
  stop,
  isInvalid,
  routeStops,
  isAssignedCard,
  onRemoveItem,
  onAddItem,
}) => {
  const { t } = useTranslation();
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);
  const { getErrorMessage } = useStopValidation(stop, routeStops);

  const errorTooltipContent = (
    <div
      className={`${STYLE_CLASSES.TOOLTIP} ${showErrorTooltip ? "block" : "hidden"}`}
    >
      {getErrorMessage()}
    </div>
  );

  const timingInfo = formatTimingInfo(stop, t);

  return (
    <Inline
      gap={2}
      justify="between"
      className="w-full min-w-0 max-w-full h-full overflow-hidden"
    >
      <Inline gap={2} align="start" className="min-w-0 flex-1 overflow-hidden">
        {!stop.isAvailable && (
          <div className="flex flex-col items-center">
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full 
              ${stop.isPickup ? "bg-pickup-muted text-pickup" : "bg-delivery-muted text-delivery"} 
              font-medium text-sm`}
            >
              {stop.stopNumber}
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0 space-y-1">
          <Inline gap={2} justify="between" className="min-w-0">
            <Inline gap={1.5} className="min-w-0 flex-1 overflow-hidden">
              <HiLocationMarker className="h-3.5 w-3.5 text-muted shrink-0" />
              <p
                className={`text-sm font-medium truncate ${isInvalid ? "text-red-500 dark:text-red-400" : "text-text"}`}
              >
                {stop.location.addressLine}
              </p>
            </Inline>
            <div className="shrink-0">
              <StatusBadge
                status={
                  stop.isPickup
                    ? t("planner:route_planner.pickup")
                    : t("planner:route_planner.delivery")
                }
                bgStyle={stop.isPickup ? statusPickup : statusDelivery}
                rounded
              />
            </div>
          </Inline>

          <Inline gap={2} justify="between" className="min-w-0">
            <Inline gap={1.5} className="min-w-0 flex-1 overflow-hidden">
              <HiOfficeBuilding className="h-3 w-3 text-muted shrink-0" />
              <p className="text-xs text-muted truncate">
                {stop.orderDisplayName}
              </p>
            </Inline>
            {timingInfo && (
              <Inline gap={1.5} className="min-w-0 max-w-[45%] shrink-0">
                <HiClock className="h-3 w-3 text-muted shrink-0" />
                <p className="text-xs text-muted truncate">
                  {timingInfo}
                </p>
              </Inline>
            )}
          </Inline>
        </div>
      </Inline>

      <Inline gap={1.5} className="shrink-0">
        {isAssignedCard && onRemoveItem && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveItem();
            }}
            className="p-1 rounded-full text-lgb-red-300 hover:bg-danger-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-lgb-red-200"
            aria-label={t(
              "planner:route_planner.remove_from_route",
              "Remove from route",
            )}
          >
            <HiMinusCircle className="w-5 h-5" />
          </button>
        )}
        {!isAssignedCard && onAddItem && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddItem();
            }}
            className="p-1 rounded-full text-brand hover:bg-brand-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-lgb-blue-200"
            aria-label={t("planner:route_planner.add_to_route", "Add to route")}
          >
            <HiPlusCircle className="w-5 h-5" />
          </button>
        )}
        {isInvalid && (
          <div className="relative">
            <div
              onMouseEnter={() => setShowErrorTooltip(true)}
              onMouseLeave={() => setShowErrorTooltip(false)}
            >
              <HiExclamation className="h-4 w-4 text-red-500 cursor-pointer" />
            </div>
            {errorTooltipContent}
          </div>
        )}
      </Inline>
    </Inline>
  );
};

export const StopCardWrapper: FC<StopCardProps> = ({
  stop,
  isDragging,
  onMoveToRoute,
  onMoveToAvailable,
  isInvalid,
  routeStops,
  onRemoveItem,
  isAssignedCard,
  onAddItem,
}) => {
  return (
    <div
      className={`${STYLE_CLASSES.CARD} w-full min-w-0 max-w-full overflow-hidden p-2.5 min-h-[54px] ${isDragging ? "opacity-70 scale-105 shadow-lg" : ""}
      ${isInvalid ? "border-red-200 dark:border-red-800" : ""}
      ${!stop.isAvailable ? `border-l-4 ${stop.isPickup ? "border-l-lgb-blue-300" : "border-l-lgb-green-300"}` : ""}
      transition-all duration-150 ease-in-out`}
    >
      <StopCardContent
        stop={stop}
        onMoveToRoute={onMoveToRoute}
        onMoveToAvailable={onMoveToAvailable}
        isInvalid={isInvalid}
        routeStops={routeStops}
        onRemoveItem={onRemoveItem}
        isAssignedCard={isAssignedCard}
        onAddItem={onAddItem}
      />
    </div>
  );
};
